/**
 * SaveToolboxModal (#638).
 *
 * Saves the current edit-mode tool set (enabled + ordered keys) as a named
 * toolbox profile. Mirrors SavePresetModal: portal overlay, name input,
 * admin-only "global" option, fail-fast error display. A client-side
 * uniqueness pre-check mirrors the server's per-user unique-name constraint so
 * the user gets immediate feedback; the server still enforces it.
 *
 * Reuses the existing `save-preset-modal-*` SCSS classes for a consistent look.
 */

import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faGlobe, faUser } from '@fortawesome/free-solid-svg-icons';
import { useToolboxProfile } from '../../../context/ToolboxProfileContext';
import '../../layerDrawer/modals/SavePresetModal.scss';
import AppModal from '../../../components/common/AppModal';

interface SaveToolboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The enabled + ordered tool keys to persist. */
  tools: string[];
  /** Called after a successful save. */
  onSaved?: () => void;
  isAdmin?: boolean;
}

export const SaveToolboxModal: React.FC<SaveToolboxModalProps> = ({
  isOpen,
  onClose,
  tools,
  onSaved,
  isAdmin = false,
}) => {
  const { profiles, save } = useToolboxProfile();
  const [name, setName] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a name for the toolbox');
      return;
    }
    // Client-side uniqueness pre-check (server enforces too).
    if (profiles.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`A toolbox named "${trimmed}" already exists`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await save({ name: trimmed, is_global: isGlobal, profile_data: { tools } });
      setName('');
      setIsGlobal(false);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save toolbox');
    } finally {
      setSaving(false);
    }
  }, [name, profiles, save, isGlobal, tools, onSaved, onClose]);

  const handleKeyDown = useCallback(
    // Escape is AppModal's now (#968) — it called onClose, which is what AppModal does.
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && e.ctrlKey) handleSave();
    },
    [handleSave],
  );

  if (!isOpen) return null;

  return (
    <AppModal
      isOpen
      onClose={onClose}
      width={480}
      maxHeight="90vh"
      header={<h3>Save Toolbox</h3>}
    >
      {/* Keydown stays on this wrapper rather than the removed overlay: the handler
          was never reached by focus, only by events bubbling up from the fields
          inside, and this element still receives those. */}
      <div className="save-preset-modal" onKeyDown={handleKeyDown}>
        <div className="save-preset-modal-body">
          {error && <div className="save-preset-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="toolbox-name">Name *</label>
            <input
              id="toolbox-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Field Recon"
              maxLength={255}
              autoFocus
            />
          </div>

          {isAdmin && (
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                />
                <FontAwesomeIcon icon={faGlobe} />
                <span>Make this the global default (visible to all users)</span>
              </label>
            </div>
          )}

          <div className="preset-type-indicator">
            <FontAwesomeIcon icon={isGlobal ? faGlobe : faUser} />
            <span>
              {isGlobal
                ? 'This toolbox will be the shared global default'
                : 'This toolbox will be private to you'}
            </span>
          </div>

          <div className="preset-type-indicator">
            <span>{tools.length} tool{tools.length === 1 ? '' : 's'} enabled</span>
          </div>
        </div>

        <div className="save-preset-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            <FontAwesomeIcon icon={faSave} />
            <span>{saving ? 'Saving...' : 'Save Toolbox'}</span>
          </button>
        </div>
      </div>
    </AppModal>
  );
};
